import { useState, useEffect } from 'react';
import api from '../lib/api';
import { getErrorMessage } from '../utils/errorUtils';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Loader2, Plus, Pencil, Trash2 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const Mantenedor = () => {
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);

  // Categories State
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ name: '' });
  const [categorySubmitting, setCategorySubmitting] = useState(false);

  // Units State
  const [unitDialogOpen, setUnitDialogOpen] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [unitForm, setUnitForm] = useState({ name: '', abbreviation: '' });
  const [unitSubmitting, setUnitSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [catsRes, unitsRes] = await Promise.all([
        api.get('/catalog/categories'),
        api.get('/catalog/units'),
      ]);
      setCategories(catsRes.data);
      setUnits(unitsRes.data);
    } catch (error) {
      toast.error('Error al cargar datos del catálogo');
    } finally {
      setLoading(false);
    }
  };

  // --- Categories Handlers ---
  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    if (!categoryForm.name) {
      toast.error('El nombre es obligatorio');
      return;
    }

    setCategorySubmitting(true);
    try {
      if (editingCategory) {
        await api.put(`/catalog/categories/${editingCategory.category_id}`, categoryForm);
        toast.success('Categoría actualizada exitosamente');
      } else {
        await api.post('/catalog/categories', categoryForm);
        toast.success('Categoría creada exitosamente');
      }
      setCategoryDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al guardar categoría'));
    } finally {
      setCategorySubmitting(false);
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar esta categoría?')) return;
    try {
      await api.delete(`/catalog/categories/${id}`);
      toast.success('Categoría eliminada');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al eliminar categoría'));
    }
  };

  // --- Units Handlers ---
  const handleUnitSubmit = async (e) => {
    e.preventDefault();
    if (!unitForm.name || !unitForm.abbreviation) {
      toast.error('Todos los campos son obligatorios');
      return;
    }

    setUnitSubmitting(true);
    try {
      if (editingUnit) {
        await api.put(`/catalog/units/${editingUnit.unit_id}`, unitForm);
        toast.success('Unidad actualizada exitosamente');
      } else {
        await api.post('/catalog/units', unitForm);
        toast.success('Unidad creada exitosamente');
      }
      setUnitDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al guardar unidad'));
    } finally {
      setUnitSubmitting(false);
    }
  };

  const handleDeleteUnit = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar esta unidad?')) return;
    try {
      await api.delete(`/catalog/units/${id}`);
      toast.success('Unidad eliminada');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al eliminar unidad'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Manrope']">Mantenedor</h1>
        <p className="text-slate-500 mt-1">Gestión de categorías y unidades de productos</p>
      </div>

      <Tabs defaultValue="categories" className="w-full">
        <TabsList className="grid w-full grid-cols-2 lg:w-[400px]">
          <TabsTrigger value="categories">Categorías</TabsTrigger>
          <TabsTrigger value="units">Unidades</TabsTrigger>
        </TabsList>

        {/* Categorías Tab */}
        <TabsContent value="categories" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-['Manrope']">Categorías de Producto</CardTitle>
              <Dialog open={categoryDialogOpen} onOpenChange={(open) => {
                if (open) {
                  setCategoryForm({ name: '' });
                  setEditingCategory(null);
                }
                setCategoryDialogOpen(open);
              }}>
                <DialogTrigger asChild>
                  <Button className="bg-teal-600 hover:bg-teal-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Nueva Categoría
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{editingCategory ? 'Editar Categoría' : 'Nueva Categoría'}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCategorySubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="category_name">Nombre *</Label>
                      <Input
                        id="category_name"
                        value={categoryForm.name}
                        onChange={(e) => setCategoryForm({ name: e.target.value })}
                        placeholder="Ej: Alimentos, Aseo..."
                      />
                    </div>
                    <div className="flex justify-end gap-3 pt-4">
                      <Button type="button" variant="outline" onClick={() => setCategoryDialogOpen(false)}>
                        Cancelar
                      </Button>
                      <Button type="submit" className="bg-teal-600 hover:bg-teal-700" disabled={categorySubmitting}>
                        {categorySubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : 'Guardar'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {categories.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={2} className="text-center py-8 text-slate-500">
                        No hay categorías registradas
                      </TableCell>
                    </TableRow>
                  ) : (
                    categories.map((category) => (
                      <TableRow key={category.category_id}>
                        <TableCell className="font-medium">{category.name}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                            onClick={() => {
                              setEditingCategory(category);
                              setCategoryForm({ name: category.name });
                              setCategoryDialogOpen(true);
                            }}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDeleteCategory(category.category_id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Unidades Tab */}
        <TabsContent value="units" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-['Manrope']">Unidades de Medida</CardTitle>
              <Dialog open={unitDialogOpen} onOpenChange={(open) => {
                if (open) {
                  setUnitForm({ name: '', abbreviation: '' });
                  setEditingUnit(null);
                }
                setUnitDialogOpen(open);
              }}>
                <DialogTrigger asChild>
                  <Button className="bg-teal-600 hover:bg-teal-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Nueva Unidad
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{editingUnit ? 'Editar Unidad' : 'Nueva Unidad'}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleUnitSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="unit_name">Nombre *</Label>
                      <Input
                        id="unit_name"
                        value={unitForm.name}
                        onChange={(e) => setUnitForm({ ...unitForm, name: e.target.value })}
                        placeholder="Ej: Kilogramos, Unidades..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="unit_abb">Abreviación *</Label>
                      <Input
                        id="unit_abb"
                        value={unitForm.abbreviation}
                        onChange={(e) => setUnitForm({ ...unitForm, abbreviation: e.target.value })}
                        placeholder="Ej: kg, un..."
                      />
                    </div>
                    <div className="flex justify-end gap-3 pt-4">
                      <Button type="button" variant="outline" onClick={() => setUnitDialogOpen(false)}>
                        Cancelar
                      </Button>
                      <Button type="submit" className="bg-teal-600 hover:bg-teal-700" disabled={unitSubmitting}>
                        {unitSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : 'Guardar'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Abreviación</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {units.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center py-8 text-slate-500">
                        No hay unidades registradas
                      </TableCell>
                    </TableRow>
                  ) : (
                    units.map((unit) => (
                      <TableRow key={unit.unit_id}>
                        <TableCell className="font-medium">{unit.name}</TableCell>
                        <TableCell>{unit.abbreviation}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                            onClick={() => {
                              setEditingUnit(unit);
                              setUnitForm({ name: unit.name, abbreviation: unit.abbreviation });
                              setUnitDialogOpen(true);
                            }}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDeleteUnit(unit.unit_id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Mantenedor;
